// ============================================================
// useDailyMeetings — live data hook for the Daily Meetings workroom.
//
// Honesty: this hook NEVER seeds demo meetings. The list comes only from
// the API. When there is no bearer token (not signed into the live
// cockpit) it reports authRequired; a missing migration or backend
// surfaces as an honest error; an empty result is an honest empty state.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { ApiError, isAuthError } from '../../../../api/client'
import {
  listDailyMeetings,
  createDailyMeeting,
  updateDailyMeeting,
  type DailyMeetingRecord,
  type DailyMeetingDraft,
} from './dailyMeetings'

export interface DailyMeetingsState {
  meetings: DailyMeetingRecord[]
  loading: boolean
  error: string | null
  /** True when the failure is "no bearer token" — the pre-sign-in state. */
  authRequired: boolean
}

function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    const msg = err.message || err.code
    if (/relation .* does not exist/i.test(msg) || /could not find the table/i.test(msg) || /list_failed/i.test(err.code)) {
      return 'The Daily Meetings backend is not migrated yet (apply migration 20260709000001).'
    }
    return msg
  }
  return 'Could not reach the Daily Meetings backend.'
}

export function useDailyMeetings() {
  const [state, setState] = useState<DailyMeetingsState>({ meetings: [], loading: true, error: null, authRequired: false })

  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const meetings = await listDailyMeetings(signal)
      if (signal?.aborted) return
      setState({ meetings, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) {
        setState({ meetings: [], loading: false, error: null, authRequired: true })
      } else {
        setState({ meetings: [], loading: false, error: humanizeError(err), authRequired: false })
      }
    }
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    load(ac.signal)
    return () => ac.abort()
  }, [load])

  const create = useCallback(async (draft: DailyMeetingDraft): Promise<DailyMeetingRecord> => {
    const meeting = await createDailyMeeting(draft)
    await load()
    return meeting
  }, [load])

  const update = useCallback(async (id: string, patch: Partial<DailyMeetingDraft>): Promise<DailyMeetingRecord> => {
    const meeting = await updateDailyMeeting(id, patch)
    await load()
    return meeting
  }, [load])

  return { ...state, reload: () => load(), create, update }
}
